/**
 * If the given string includes a 'T', assume it's a full timestamp.
 * Otherwise, treat it as a date-only in local time with no shift.
 */
export function parseDateString(raw: string): Date {
  if (!raw) return new Date(NaN); // invalid
  // If it looks like a full timestamp (e.g., 2025-02-18T13:00:00Z):
  if (raw.includes('T')) {
    return new Date(raw);
  } else {
    // If it's only "YYYY-MM-DD", parse as local date:
    // This ensures the day is preserved in local timezone.
    const [yyyy, mm, dd] = raw.split('-').map(Number);
    // month is 0-based in JS, so subtract 1 from mm
    return new Date(yyyy, (mm || 1) - 1, dd || 1);
  }
}

/**
 * Formats a date string (timestamp or "YYYY-MM-DD") into a localized date string.
 * The "dateStyle" can be 'short' | 'medium' | 'long', controlling how many details
 * to show in the output.
 */
export function formatDateString(
  raw: string,
  localeOrFormat: string = 'en-US',
  dateStyle: 'short' | 'medium' | 'long' = 'short'
): string {
  if (!raw) return '';
  const date = parseDateString(raw);
  if (isNaN(date.getTime())) return '';

  // Check if localeOrFormat looks like a format string (simple check)
  if (localeOrFormat.includes('/') || localeOrFormat.includes(':')) {
    // Handle custom format 'MM/dd/yyyy hh:mm a'
    if (localeOrFormat === 'MM/dd/yyyy hh:mm a') {
      const optionsDate: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      };
      const optionsTime: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      };
      // Note: This might not be perfectly robust for all locales if 'en-US' isn't desired
      // but matches the requested format string.
      return `${date.toLocaleDateString('en-US', optionsDate)} ${date.toLocaleTimeString('en-US', optionsTime)}`;
    } else {
      // Fallback for other unknown format strings - return ISO or basic string?
      // For now, just return basic short date if format is unrecognized
      console.warn(`Unsupported format string: ${localeOrFormat}. Using default short date.`);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'numeric', day: 'numeric' });
    }
  } else {
    // Original logic for locale + dateStyle
    const options: Intl.DateTimeFormatOptions = {};
    switch (dateStyle) {
      case 'short':
        // e.g. "10/14/2025"
        options.year = 'numeric';
        options.month = 'numeric';
        options.day = 'numeric';
        break;
      case 'medium':
        // e.g. "Oct 14, 2025"
        options.year = 'numeric';
        options.month = 'short';
        options.day = 'numeric';
        break;
      case 'long':
        // e.g. "October 14, 2025"
        options.year = 'numeric';
        options.month = 'long';
        options.day = 'numeric';
        break;
    }

    return date.toLocaleDateString(localeOrFormat, options);
  }
}

/**
 * Joins non-empty class names with a space.
 * (Used by various UI components for conditional classes.)
 */
export function cn(...inputs: (string | undefined | null | false)[]): string {
  return inputs.filter(Boolean).join(' ');
}